import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Menu,
  UserRound,
  LogOut,
  Plus,
  ChevronDown,
  Settings as SettingsIcon,
  Shield,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Separator } from '@/components/ui/separator';
import { useAuth } from '../../contexts/AuthContext';
import OrgKontekstBanner from './OrgKontekstBanner';
import KontekstEndretDialog from './KontekstEndretDialog';

const AppHeader: React.FC = () => {
  const navigate = useNavigate();
  const { user, logout, isAdmin, hasRole } = useAuth();
  const isAdminUser = isAdmin();
  const isSuperAdmin = hasRole('superadmin');
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = React.useState(false);
  const profileMenuRef = React.useRef<HTMLDivElement>(null);

  const initials = `${user?.firstName?.[0] ?? ''}${user?.lastName?.[0] ?? ''}`.trim() || 'TA';
  React.useEffect(() => {
    const handler = (event: MouseEvent) => {
      if (!profileMenuRef.current) return;
      if (!profileMenuRef.current.contains(event.target as Node)) {
        setProfileMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleNewProduction = () => {
    navigate('/dashboard?nyProduksjon=1');
    setMobileOpen(false);
  };

  const ProfileActions = () => (
    <div className="space-y-1">
      <button
        className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100"
        onClick={() => {
          setProfileMenuOpen(false);
          setMobileOpen(false);
          navigate('/profile');
        }}
      >
        <UserRound className="h-4 w-4" />
        Profil
      </button>
      {isAdminUser && (
        <button
          className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100"
          onClick={() => {
            setProfileMenuOpen(false);
            setMobileOpen(false);
            navigate('/settings');
          }}
        >
          <SettingsIcon className="h-4 w-4" />
          Innstillinger
        </button>
      )}
      {isSuperAdmin && (
        <button
          className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100"
          onClick={() => {
            setProfileMenuOpen(false);
            setMobileOpen(false);
            navigate('/superadmin');
          }}
        >
          <Shield className="h-4 w-4" />
          SuperAdmin
        </button>
      )}
      <button
        className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50"
        onClick={handleLogout}
      >
        <LogOut className="h-4 w-4" />
        Logg ut
      </button>
    </div>
  );

  return (
    <>
    <header className="relative z-50 w-full text-white shadow-sm">
      <div style={{ backgroundColor: '#0d4249' }}>
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-3 px-3 py-3 sm:px-6 sm:py-4">
          <button
            className="flex items-center gap-2 text-lg font-semibold tracking-tight sm:text-xl"
            onClick={() => navigate('/dashboard')}
          >
            <img src="/logo.png" alt="" className="h-9 w-9 object-contain" />
            Voluplan
          </button>
          <div className="flex items-center gap-2">
            {isAdminUser && (
              <Button
                onClick={handleNewProduction}
                className="hidden w-full gap-2 rounded-full bg-white text-primary shadow-sm hover:bg-white/95 sm:inline-flex"
              >
                <Plus className="h-4 w-4" />
                Ny produksjon
              </Button>
            )}
            <div
              ref={profileMenuRef}
              className="relative hidden lg:block"
            >
              <button
                onClick={() => setProfileMenuOpen((prev) => !prev)}
                className="flex items-center gap-2 rounded-full bg-white/10 px-2 py-1 text-sm font-medium hover:bg-white/20"
              >
                <Avatar className="h-9 w-9 border border-white/40 bg-primary/20">
                  <AvatarFallback className="bg-transparent text-white">
                    {initials}
                  </AvatarFallback>
                </Avatar>
                <div className="hidden text-left leading-tight sm:block">
                  <span className="block text-xs text-white/80">
                    {isSuperAdmin ? 'SuperAdmin' : isAdminUser ? 'Administrator' : 'Bruker'}
                  </span>
                  <span className="block text-sm font-semibold">
                    {user?.firstName} {user?.lastName}
                  </span>
                </div>
                <ChevronDown className="h-4 w-4 text-white/80" />
              </button>
              {profileMenuOpen && (
                <div className="absolute right-0 mt-2 w-64 rounded-2xl border border-slate-100 bg-white text-slate-900 shadow-xl overflow-hidden z-[100]">
                  {user?.organizationName && (
                    <div className="bg-slate-100 px-3 py-2 border-b border-slate-100">
                      <p className="text-xs font-medium text-slate-600 uppercase tracking-wide">
                        {user.organizationName}
                      </p>
                    </div>
                  )}
                  <div className="p-3">
                    <div className="mb-3">
                      <p className="text-sm font-semibold">
                        {user?.firstName} {user?.lastName}
                      </p>
                      <p className="text-xs text-slate-500">
                        {user?.email}
                      </p>
                    </div>
                    <ProfileActions />
                  </div>
                </div>
              )}
            </div>
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button
                  variant="ghost"
                  className="inline-flex items-center gap-2 text-white hover:bg-white/15 lg:hidden"
                >
                  <Menu className="h-5 w-5" />
                  <span className="sr-only">Åpne meny</span>
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-full sm:max-w-sm p-0">
                <SheetTitle className="sr-only">Meny</SheetTitle>
                <SheetDescription className="sr-only">Navigasjonsmeny</SheetDescription>
                {user?.organizationName && (
                  <div className="bg-slate-100 px-5 py-3 border-b border-slate-200">
                    <p className="text-xs font-medium text-slate-600 uppercase tracking-wide">
                      {user.organizationName}
                    </p>
                  </div>
                )}
                <div className="space-y-6 p-5">
                  <div className="flex items-center gap-3">
                    <Avatar className="h-12 w-12 bg-primary/10 text-primary">
                      <AvatarFallback>{initials}</AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="text-base font-semibold">
                        {user?.firstName} {user?.lastName}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {user?.email}
                      </p>
                    </div>
                  </div>
                  {isAdminUser && (
                    <Button className="w-full gap-2" onClick={handleNewProduction}>
                      <Plus className="h-4 w-4" />
                      Ny produksjon
                    </Button>
                  )}
                  <Separator />
                  <ProfileActions />
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>

    </header>
    <OrgKontekstBanner />
    <KontekstEndretDialog />
    </>
  );
};

export default AppHeader;

